import React from "react";

import { formatFractionInput, useTranslation } from "utils";

import { FarmFootprintReturnBody, FootprintingFarmFormType } from "models";

import { Card, ListCard } from "components";

import { displayRadioAnswerSlug, getLandUseChangeSlugForFarmFootprint } from "pages/app/footprinting/components/FootprintingFarmReview";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";

export const FootprintEditFarmDetailsCard = ({
  farmFootprintData,
  isFarmFootprintDataLoading,
  setShowEditModal,
  setFarmModalForm,
}: {
  farmFootprintData: FarmFootprintReturnBody;
  isFarmFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setFarmModalForm: (formType: FootprintingFarmFormType) => void;
}) => {
  const { t } = useTranslation("footprinting");

  const handleEditForm = (formType: Exclude<FootprintingFarmFormType, null>) => {
    setShowEditModal(true);
    setFarmModalForm(formType);
  };

  return (
    <Card className="admin-edit-farm-details-card padding-125">
      <ReviewPageTitle title={`${t("review.farm.title")} - ${farmFootprintData?.year}`} iconName="klim/farm" />
      {/* Farm soil form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.farm.soil")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("soil") }}
        dataPairs={[
          {
            label: t("soilForm.soilTypeLabel"),
            value: farmFootprintData?.footprint?.overallSoilType ? t(`soilForm.soilTypeOptions.${farmFootprintData?.footprint?.overallSoilType}`) : "",
          },
          {
            label: t("soilForm.soilSaturationLabel"),
            value: farmFootprintData?.footprint?.averageSoilSaturation
              ? t(`soilForm.soilSaturationOptions.${farmFootprintData?.footprint?.averageSoilSaturation}`)
              : "",
          },
        ]}
      />
      {/* Farm electricity form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("navigation.farm.electricity")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("electricity") }}
        dataPairs={[
          { label: t("electricityForm.question.total"), value: `${farmFootprintData?.footprint?.electricityKwh} kWh` },
          { label: t("electricityForm.question.renewables"), value: `${formatFractionInput(farmFootprintData?.footprint?.renewablesPct ?? 0)} %` },
        ]}
      />
      {/* Farm waste form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("navigation.farm.waste")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("waste") }}
        dataPairs={[
          { label: t("wasteForm.wasteQuestionLabels"), value: `${farmFootprintData?.footprint?.wasteKgs} kg` },
          { label: t("wasteForm.wasteWaterQuestionLabel"), value: `${farmFootprintData?.footprint?.wasteWaterM3} m³` },
        ]}
      />
      {/* Farm fuel form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("navigation.farm.fuel")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("fuel") }}
        dataPairs={[
          { label: t("fuelForm.question.dieselQuestionLabel"), value: `${farmFootprintData?.footprint?.dieselL} L` },
          { label: t("fuelForm.question.petrolQuestionLabel"), value: `${farmFootprintData?.footprint?.petrolL} L` },
          { label: t("fuelForm.question.naturalGasQuestionLabel"), value: `${farmFootprintData?.footprint?.naturalGasM3} m³` },
          { label: t("fuelForm.question.heatingOilQuestionLabel"), value: `${farmFootprintData?.footprint?.heatingOilL} L` },
        ]}
      />
      {/* Farm refrigerants form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("navigation.farm.cooling")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("cooling") }}
        dataPairs={[
          {
            label: t("coolingForm.question.refrigerantsUsed"),
            value: t(displayRadioAnswerSlug(farmFootprintData?.footprint?.refrigerantsActive ?? null)),
            children: farmFootprintData?.footprint?.refrigerantsActive
              ? farmFootprintData?.footprint?.refrigerantUsage.map((usedRefrigerant, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    isLoading={isFarmFootprintDataLoading}
                    title={t("coolingForm.question.title") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("coolingForm.question.refrigerantType"),
                        value: usedRefrigerant.refrigerantType ?? "",
                      },
                      {
                        label: t("coolingForm.question.refrigerantAmount"),
                        value: `${usedRefrigerant.amountUsedKg} kg`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Farm transport form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("navigation.farm.transport")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("transport") }}
        dataPairs={[
          {
            label: t("farmTransportForm.question.radioQuestion"),
            value: t(displayRadioAnswerSlug(farmFootprintData?.footprint?.purchasesActive ?? null)),
            children: farmFootprintData?.footprint?.purchasesActive
              ? farmFootprintData?.footprint?.purchases.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    isLoading={isFarmFootprintDataLoading}
                    title={t("farmTransportForm.retailer", { count: index + 1 })}
                    dataPairs={[
                      {
                        label: t("farmTransportForm.question.retailerNameQuestionLabel"),
                        value: i.retailerName ?? "",
                      },
                      {
                        label: t("farmTransportForm.question.countryQuestionLabel"),
                        value: i.retailerCountryCode ?? "",
                      },
                      {
                        label: t("farmTransportForm.question.postcodeQuestionLabel"),
                        value: i.retailerPostCode ?? "",
                      },
                      {
                        label: t("farmTransportForm.question.mineralFertilizerQuestionLabel"),
                        value: `${formatFractionInput(i.mineralFertilizerPct)} %`,
                      },
                      {
                        label: t("farmTransportForm.question.organicFertilizerQuestionLabel"),
                        value: `${formatFractionInput(i.organicFertilizerPct)} %`,
                      },
                      {
                        label: t("farmTransportForm.question.seedsQuestionLabel"),
                        value: `${formatFractionInput(i.seedsPct)} %`,
                      },
                      {
                        label: t("farmTransportForm.question.plantProtectionQuestionLabel"),
                        value: `${formatFractionInput(i.plantProtectionPct)} %`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Land Use Change form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isFarmFootprintDataLoading}
        title={t("navigation.farm.landuse")}
        titleButton={{ label: t("review.edit"), onClick: () => handleEditForm("landuse") }}
        dataPairs={[
          {
            label: t("landUseForm.question.general"),
            value: t(getLandUseChangeSlugForFarmFootprint(farmFootprintData?.footprint)),
            children:
              (farmFootprintData?.footprint?.landUseChangeUsage.length ?? 0) > 0
                ? farmFootprintData?.footprint?.landUseChangeUsage
                    .filter((luc) => luc.hasLandChanged === "yesLUC" && (luc.areaConvertedHa ?? 0) > 0)
                    .map((luc, index) => (
                      <ListCard
                        key={luc.id}
                        isSublist
                        isLoading={isFarmFootprintDataLoading}
                        title={t("landUseForm.question.title") + (index + 1)}
                        dataPairs={[
                          {
                            label: t("landUseForm.question.previousLandChange"),
                            value: luc.previousLandUseType ?? "",
                          },
                          {
                            label: t("landUseForm.question.conversionYear"),
                            value: `${luc.yearOfConversion}`,
                          },
                          {
                            label: t("landUseForm.question.areaConverted"),
                            value: `${luc.areaConvertedHa} Ha`,
                          },
                        ]}
                      />
                    ))
                : null,
          },
        ]}
      />
    </Card>
  );
};
